<template>
  <div class="w-full flex flex-col mb-2 relative">
    <label
      :for="id"
      :class="[
        'mb-1 font-medium text-sm',
        disable ? 'text-gray-300' : 'text-gray-600'
      ]"
    >
      {{ placeholder }}
      <span v-if="required && !disable" class="text-red-600"> *</span>
    </label>

    <div class="flex items-center border-2 rounded-md bg-white focus-within:ring-2 focus-within:ring-theme-primary">
      <input type="text" placeholder="+63" v-show="text_type=='mobile'" disabled class="w-full h-[40px] max-w-xxs text-center bg-gray-50 input-field__static border-r-2"/>
      <input v-if="id=='tin'" 
          autocomplete="off"
          :id="id" 
          ref="input"
          :type="'tel'" 
          v-model="curr_val"
          v-mask="'###-###-###'"
          @paste="pasted"
          @keypress="checkEmpty"
          @keyup.enter="enter"
          @focus="onFocus"
          @blur="onBlur"
          class="w-full max-w-full bg-transparent appearance-none leading-6 p-2 text-gray-700 text-base focus:outline-none"  
      />
      <input v-else-if="id=='date'"
        :id="id"
        ref="input"
        type="date"
        :max="today"
        v-model="curr_val"
        class="w-full max-w-full bg-transparent appearance-none leading-6 p-2 text-gray-700 text-base focus:outline-none"
      />
      <input v-else
          :id="id" 
          ref="input"
          autocomplete="off"
          @input="uppercase ? curr_val = $event.target.value.toUpperCase() : curr_val = $event.target.value"
          :type="text_type=='mobile'?'number':text_type" 
          v-model="curr_val"
          :disabled="disable"
          @keyup.backspace="backspace"
          @keypress="speckeydown"
          @keyup.enter="enter"
          @focus="onFocus"
          @blur="onBlur"
          @paste="pasted"
          class="w-full max-w-full bg-transparent appearance-none min-h-[40px] leading-6 p-2 text-gray-700 text-base focus:outline-none"  
      />
      <div v-if="has_icon" class="px-3 flex justify-center items-center align-middle">
        <div v-if="strength && curr_val" class="whitespace-nowrap text-sm">
          <PasswordStrength v-bind:value="curr_val" @score="passwordStrength" />
        </div>
          <div class="w-icon-24 h-icon-24 flex justify-center items-center cursor-pointer">
            <Icons id="textbox_icon" v-bind:source="getImgSrc()" @click="togglePeeker" :width="22" />
          </div>
       
      </div>

      <div v-if="validateOnInput && (field_loading || field_success || field_error)" class="pr-2 flex absolute right-0">
        <SpinnerControl v-if="field_loading" class="w-6 h-6"/>
        <img v-if="field_success" src="@/assets/images/papsi_check_icon.svg" class="w-8 h-8 lg:w-6 lg:h-6">
        <img v-if="field_error" src="@/assets/images/papsi_error_icon.svg" class="w-6 h-6">
      </div>
    </div>
    <div v-if="error_message && (id =='email' || id=='mobile' || id=='tin')" class="text-red-600 mt-2 text-sm">
      {{error_message}}
    </div>
    <div v-if="validateOnInput && availability_error" class="text-red-600 mt-2 text-sm">
      {{ availability_error }}
    </div>
    <div v-if="valid_length" class="text-red-600 mt-1">
      Please enter {{max_length + 1}} characters
    </div>
    <div v-if="validateOnInput && validation_message" class="text-red-600 mt-2 text-sm">
      {{ validation_message }}
    </div>

  </div>
</template>

<script>
import { defineComponent } from 'vue';
import Icons from './Icon.vue';
import PasswordStrength from './PasswordStrength.vue';
import User from '@/services/User';
import SpinnerControl from './SpinnerControl.vue';
export default defineComponent({
    components:{
      Icons,
      PasswordStrength,
      SpinnerControl
    },
    props:{
            id: {
                type:String,
                required:true,
            },
            type: {
                type:String,
                required:false,
                default:'text'
            },
            placeholder: {
                type:String,
                required:false,
            },
            initial:{
                type:String,
                required:false,
                default: ''
            },
            has_error: {
                type:Boolean,
                required:false,
            },
            peekable: {
                type: Boolean,
                required: false,
                default: false,
            },
            strength: {
                type: Boolean,
                required: false,
                default: false,
            },
            uppercase:{
              type:Boolean,
              required: false,
            },
            nonumbers:{
              type:Boolean,
              required:false,
            },
            nospecial:{
              type:Boolean,
              required:false,
            },
            max_length: {
              type:Number,
              required:false,
            },
            required:{
              type:Boolean,
              required: false,
            },
            nopaste:{
              type:Boolean,
              required:false,
            },
            disable:{
              type:Boolean,
              required:false,
            },
            confirmWith: {
              type: String,
              required: false,
              default: ''
            },
            validateOnInput: {
              type: Boolean,
              default: false
            }
    },
    data(){
        return {
            last_checked_value: '',
            last_checked_result: null,
            field_loading: false,
            field_success: false,
            field_error: false,
            curr_val: '',
            validation_message: '',
            validation_status: '',
            availability_error: '',
            value:'',
            text_type:'text',
            isFocused:false,
            empty:false,
            has_icon: false,
            on_focus_theme:false,
            visible: false,
            icon:'',
            invalid_email:false,
            invalid_number:false,
            error_message:'',
            valid_length:false,
        }
    },
    watch: {
        curr_val(value) {
          if (value) {
            if (this.validateOnInput) {
              this.validateField(value);
            }
            this.$emit("changed", {
              key: this.id,
              value: value,
            });
            this.value = value;
          } else {
            // Clear all error messages when field is empty
            this.validation_message = '';
            this.availability_error = '';
            this.field_error = false;
            this.field_success = false;
            this.field_loading = false;
            this.error_message = '';
            this.valid_length = false;
            this.last_checked_value = '';
            this.last_checked_result = null;
            this.$emit("changed", {
              key: this.id,
              value: '',
            });
            this.value = '';
          }
        },
        field_loading(val) {
          if (['username', 'email', 'mobile_number'].includes(this.id)) {
            this.$emit('validationState', {
              key: this.id,
              loading: val,
              error: this.field_error
            });
          }
        },
        field_error(val) {
          if (['username', 'email', 'mobile_number'].includes(this.id)) {
            this.$emit('validationState', {
              key: this.id,
              loading: this.field_loading,
              error: val
            });
          }
        },
    },
    mounted(){
        if(this.type) {
            this.text_type = this.type;
        }
         if(this.type == "Password" && this.peekable)
          {
              this.has_icon = true;
          }
          
        this.curr_val = this.initial;
    },
    
    methods:{
      checkAvailability() {

         if (this.validateOnInput === false) {
            return;
          }
        // Only check for these fields and only if there's a value
        if (
          !this.curr_val ||
          !['email', 'mobile_number', 'username'].includes(this.id)
        ) {
          this.field_loading = false;
          this.field_success = false;
          this.field_error = false;
          this.availability_error = '';
          this.last_checked_value = '';
          this.last_checked_result = null;
          return;
        }

        // If value hasn't changed, don't re-check, just restore last result
        if (this.curr_val === this.last_checked_value) {
          if (this.last_checked_result === 'success') {
            this.field_success = true;
            this.field_error = false;
            this.availability_error = '';
          } else if (this.last_checked_result === 'error') {
            this.field_success = false;
            this.field_error = true;
            // availability_error is already set
          }
          this.field_loading = false;
          return;
        }

        this.field_loading = true;
        this.field_success = false;
        this.field_error = false;
        this.availability_error = '';

        // For email
        if (this.id === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(this.curr_val)) {
            this.field_success = false;
            this.field_error = true;
            this.field_loading = false;
            this.last_checked_value = this.curr_val;
            this.last_checked_result = 'error';
            return;
          }
          // API call
          User.checkEmail(this.curr_val)
            .then(() => {
              this.field_success = true;
              this.field_error = false;
              this.availability_error = '';
              this.last_checked_value = this.curr_val;
              this.last_checked_result = 'success';
            })
            .catch(err => {
              this.field_success = false;
              this.field_error = true;
              this.availability_error = err.response?.data?.message || 'Email already taken';
              this.last_checked_value = this.curr_val;
              this.last_checked_result = 'error';
            })
            .finally(() => {
              this.field_loading = false;
            });
        }

        // For mobile
        if (this.id === 'mobile_number') {
          const mobileRegex = /^9\d{9}$/;
          if (!mobileRegex.test(this.curr_val)) {
            this.field_success = false;
            this.field_error = true;
            this.field_loading = false;
            this.last_checked_value = this.curr_val;
            this.last_checked_result = 'error';
            return;
          }

          User.checkMobile('+63' + this.curr_val)
            .then(() => {
              this.field_success = true;
              this.field_error = false;
              this.availability_error = '';
              this.last_checked_value = this.curr_val;
              this.last_checked_result = 'success';
            })
            .catch(err => {
              this.field_success = false;
              this.field_error = true;
              this.availability_error = 'Mobile number is unavailable. Please try another.';
              this.last_checked_value = this.curr_val;
              this.last_checked_result = 'error';
            })
            .finally(() => {
              this.field_loading = false;
            });
        }

        // For username
        if (this.id === 'username') {
          const usernameRegex = /^[a-zA-Z0-9]{6,}$/;
          if (!usernameRegex.test(this.curr_val)) {
            this.field_success = false;
            this.field_error = true;
            this.field_loading = false;
            this.last_checked_value = this.curr_val;
            this.last_checked_result = 'error';
            return;
          }
          User.checkUsername(this.curr_val)
            .then(() => {
              this.field_success = true;
              this.field_error = false;
              this.availability_error = '';
              this.last_checked_value = this.curr_val;
              this.last_checked_result = 'success';
            })
            .catch(err => {
              this.field_success = false;
              this.field_error = true;
              this.availability_error = 'Username is unavailable. Please try another.';
              this.last_checked_value = this.curr_val;
              this.last_checked_result = 'error';
            })
            .finally(() => {
              this.field_loading = false;
            });
        }
      },

      validateField(value) {
        if (!value) {
          // Show required message only if required is true
          this.validation_message = this.required ? 'This field is required.' : '';
          return;
        }

        switch (this.id) {
          case 'email': {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            this.validation_message = emailRegex.test(value)
              ? ''
              : 'Should be in a valid email address format.';
            break;
          }
          case 'username': {
            const validUsername = /^[a-zA-Z0-9]{6,}$/;
            if (value.includes(' ')) {
              this.validation_message = 'Username must not contain spaces.';
            } else if (!validUsername.test(value)) {
              this.validation_message =
                'Username must be at least six (6) characters and contain no special characters.';
            } else {
              this.validation_message = '';
            }
            break;
          }
          case 'mobile_number': {
            const mobileRegex = /^9\d{9}$/;
            this.validation_message = mobileRegex.test(value)
              ? ''
              : 'Mobile number must start with 9 and be 10 digits.';
            break;
          }
          case 'password': {
            const strongPassword = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
            this.validation_message = strongPassword.test(value)
              ? ''
              : 'Password must be strong, eight (8) or more characters, include letters, numbers, and symbols.';
            break;
          }
          case 'confirm_password': {
            if (this.confirmWith && value !== this.confirmWith) {
              this.validation_message = 'Passwords do not match.';
            } else {
              this.validation_message = '';
            }
            break;
          }
          case 'account_number': {
            this.validation_message = value.length === 6
              ? ''
              : 'Invalid Account No.';
            break;
          }
          case 'serial_number': {
            if (value.startsWith('O-')) {
              this.validation_message = value.length === 7
                ? ''
                : 'Invalid Serial No.';
            } else {
              this.validation_message = value.length === 6
                ? ''
                : 'Invalid Serial No.';
            }
            break;
          }
          case 'new_password': {
            const strongPassword = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
            this.validation_message = strongPassword.test(value)
              ? ''
              : 'Password must be strong, eight (8) or more characters, include letters, numbers, and symbols.';
            break;
          }
          case 'con_password': {
            if (this.confirmWith && value !== this.confirmWith) {
              this.validation_message = 'Passwords do not match.';
            } else {
              this.validation_message = '';
            }
            break;
          }
          default: {
            this.validation_message = '';
          }
        }
      },

        getImgSrc() {
            return !this.visible ? "images/papsi_invi_icon.svg" : "images/papsi_vis_icon.svg";
        },
        backspace(e){
          if(e.target.value.length == 0){
            this.$emit("changed", {
                key: this.id,
                value: '',
            });
            this.value = '';
          }
        },
        clickPlaceholder() {
            this.$refs.input.focus();
            this.isFocused = true;
        },
        passwordStrength(n) {
          // console.log(n);
            this.$emit('score', n);
        },
        onFocus() {
            this.isFocused= true;
            this.on_focus_theme = true;
        },
        enter() {
            this.$emit('enter');
        },
        onBlur() {
            this.isFocused= false;
            this.on_focus_theme = false;
            this.checkAvailability();
        },
        getValue() {
            return this.value != null ? this.value : this.curr_value;
        },
        checkEmpty() {
          let that = this;
          if(!that.curr_val) {
            that.empty = true;
            this.$emit('hasError',this.id);
          } else {
            that.empty = false;
            this.$emit('clearError',this.id);
          }
        },
        pasted(e) {
          let pastedValue = e.clipboardData.getData('text');
          // If max_length is set, trim the pasted value
          if (this.max_length && pastedValue.length == this.max_length) {
            pastedValue = pastedValue.slice(0, this.max_length);
          }
          // Set the value manually and prevent default paste
          this.curr_val = pastedValue;
          e.preventDefault();
        },
        togglePeeker() {
            if(this.visible)
            {
                this.text_type = "password";
                this.visible = false;
            }
            else
            {
                this.text_type = "text";
                this.visible = true;
            }
        },
        checkValidNumber() {
          if(/^(9)\d{9}$/.test(this.curr_val)) {
            this.empty = false;
            this.invalid_number = false;
          }else{
            this.empty = true;
            this.invalid_number = true;
          }
        },
        checkDuplicate() {
          let that = this;
          let payload;

          if(that.id == 'tin' && that.curr_val) {
              payload = {
              type:5,
              value:that.curr_val,
            }
          }
          if(that.id =='mobile' && that.curr_val) {
            payload = {
              type:3,
              value:'+63'+that.curr_val,
            }
          }
          if(that.id=='email' && that.curr_val) {
              payload = {
              type:2,
              value:this.curr_val,
            }
          }
          User.testData(payload).then(function(){
             that.error_message ='';
          }).catch(function(error){
             
            if(that.id=='mobile') {
              that.empty = true;
            }
            if(that.id=='email') {
              that.empty = true;
            }
            if(that.id=='tin') {
              that.empty = true;
            }
            that.error_message = error.response.data.message;
          });
        },
        validateEmail() {
          if (/^\w+([\\.-]?\w+)*@\w+([\\.-]\w+)*(\.\w{2,3})+$/.test(this.curr_val)) {
              this.invalid_email = false;
              this.empty = false;
          } else {
              this.empty = true;
              this.invalid_email = true;
          }
        },
        clearText() {
          this.curr_val = '';
        },
        checkLength(){
          // console.log(this.curr_val.length,this.max_length);
          if(this.curr_val != ''){
             if(this.curr_val.length == (this.max_length + 1)){
            this.valid_length = false;
            }else {
              this.valid_length = true;
            }
          }
        },
        speckeydown(e) {
            // console.log(this.$refs);
            this.checkEmpty();
            if (/^[*|\\":<>[\]{}`\\()'?/!.#%;^=_@&+$0,-9]*$/.test(e.key) && this.nonumbers && this.nospecial) {
                e.preventDefault();
            }
            if (this.id === 'last_name' || this.id === 'first_name') {
                // Block only number keys
                if (/^\d$/.test(e.key)) {
                    e.preventDefault();
                    return;
                }
            }
            if (this.id === 'username') {
              if (!/^[a-zA-Z0-9]$/.test(e.key)) {
                e.preventDefault();
                return;
              }
            }
            if (this.id === 'username_login') {
              // Block spaces only
              if (e.key === ' ') {
                e.preventDefault();
                return;
              }
            }
            if(this.type == 'mobile' && /^(e|E)*$/.test(e.key) ){
                 e.preventDefault();
            }
            if(/^["`']*$/.test(e.key) && this.id == 'email' ) {
               e.preventDefault();
            }
            if(/^[*|\\":<>[\]{}`\\()'?/!.#%;^=_@&+$]*$/.test(e.key) && this.nospecial) {
                e.preventDefault();
            }
            if(/^0/.test(e.key) && this.text_type == 'mobile' && e.target.value.length<1) {
                e.preventDefault();
            }
            if(e.target.value.length >this.max_length) {
              e.preventDefault();
            }
        },        
    },
    computed: {
        today() {
          const d = new Date();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${d.getFullYear()}-${month}-${day}`;
        },
        floatOn() {
          if(this.curr_val){
            return this.isFocused || this.value.toString().length > 0;
          }else {
            return this.isFocused;
          }
          
        },
    },

});
</script>
<style >

.input-field__input {
  font-family: Poppins,sans-serif;
  width: 100%;
  height: 55px !important;
  font-size: 13px;
  border: 1px solid #bcbcbc;
  border-radius: 5px;
  outline: none;
}
.input-field__static {
  width: 100%;
  /* margin-top: -10px; */

  outline: none !important;
}
.input-field__input--error {
  border: 1px solid #f00f0f !important;
}
input[type=number]::-webkit-inner-spin-button, 
input[type=number]::-webkit-outer-spin-button { 
  -webkit-appearance: none; 
  margin: 0; 
}
.input-field-wrapper {
  padding-top: 0px;
  position: relative;
}
.input-field__input {
  width: 100%;
  height: 100%;
   font-family: Poppins, sans-serif;
  font-size: 13px;
  border: 1px solid #bcbcbc;
  border-radius: 5px;
  outline: none;
}
.input-field__input--focused {
  border: 2px solid #016b00;
}
.input-field__input--empty {
  border: 1px solid #db0404;
}
.input-field__placeholder {
  position: absolute;
  font-size: 18px;
  left: 12px;
  top: 19px;
  padding: 0 5px;
  background: transparent;
  transition: all 0.3s;
  line-height: 12px;
}
.input-field-number__placeholder {
  position: absolute;
  font-size: 18px;
  left: 85px;
  top: 19x;
  padding: 0 3px;
  background: transparent;
  transition: all 0.3s;
  line-height: 12px;
}
.input-field__placeholder--focused {
  top: 3px;
  font-size: 12px;
  left: 2px;
}
.input-field-number__placeholder--focused {
  top: 3px;
  font-size: 12px;
  left: 85px;
}
.peek-margin {
    margin-top: 13px;
}
</style>
